import { round2 } from '../lib/format';
import type {
  Distribution,
  DistributionLine,
  Holder,
  LedgerEntry,
  TaxComponents,
  UnitClass,
} from '../data/types';
import { unitsAt } from './holdings';

export const TFN_WITHHOLDING_RATE = 0.47;
export const NON_RESIDENT_INTEREST_RATE = 0.1;

export const DEFAULT_COMPONENTS: TaxComponents = {
  interest: 0.92,
  otherIncome: 0.05,
  discountedGain: 0,
  taxDeferred: 0.03,
  foreignIncome: 0,
};

export interface DistributionInput {
  classId: string;
  recordDate: string;
  centsPerUnit: number;
  components: TaxComponents;
  drpEnabled: boolean;
}

export function calculateDistributionLines(
  input: DistributionInput,
  ledger: LedgerEntry[],
  holders: Holder[],
): DistributionLine[] {
  const holderIds = new Set(
    ledger.filter((entry) => entry.classId === input.classId).map((entry) => entry.holderId),
  );
  const lines: DistributionLine[] = [];
  for (const holderId of holderIds) {
    const units = unitsAt(ledger, holderId, input.classId, input.recordDate);
    if (units <= 0) continue;
    const holder = holders.find((candidate) => candidate.id === holderId);
    if (!holder) continue;
    const gross = round2((units * input.centsPerUnit) / 100);
    const tfnWithholding =
      holder.tfnStatus === 'NOT_QUOTED' && holder.residency === 'AU'
        ? round2(gross * TFN_WITHHOLDING_RATE)
        : 0;
    const nrWithholding =
      holder.residency !== 'AU'
        ? round2(gross * input.components.interest * NON_RESIDENT_INTEREST_RATE)
        : 0;
    lines.push({
      holderId,
      units,
      gross,
      tfnWithholding,
      nrWithholding,
      net: round2(gross - tfnWithholding - nrWithholding),
      method: input.drpEnabled && holder.drpElection ? 'DRP' : 'CASH',
    });
  }
  return lines.sort((left, right) => right.gross - left.gross);
}

export function distributionTotals(distribution: Pick<Distribution, 'lines'>) {
  return distribution.lines.reduce(
    (totals, line) => ({
      gross: round2(totals.gross + line.gross),
      tfnWithholding: round2(totals.tfnWithholding + line.tfnWithholding),
      nrWithholding: round2(totals.nrWithholding + line.nrWithholding),
      net: round2(totals.net + line.net),
      cash: round2(totals.cash + (line.method === 'CASH' ? line.net : 0)),
      drp: round2(totals.drp + (line.method === 'DRP' ? line.net : 0)),
      holders: totals.holders + 1,
    }),
    { gross: 0, tfnWithholding: 0, nrWithholding: 0, net: 0, cash: 0, drp: 0, holders: 0 },
  );
}

export function centsForAnnualRate(annualPct: number, frequency: number): number {
  return Math.round((annualPct / frequency) * 10_000) / 10_000;
}

export function postDistributionEntries(
  distribution: Distribution,
  unitClass: UnitClass,
  startSeq: number,
  actor: string,
): LedgerEntry[] {
  const entries: LedgerEntry[] = [];
  let seq = startSeq;
  const posted = `${distribution.paymentDate}T09:00:00`;
  for (const line of distribution.lines) {
    const base = {
      fundId: distribution.fundId,
      classId: distribution.classId,
      holderId: line.holderId,
      effectiveDate: distribution.paymentDate,
      postedAt: posted,
      eventId: distribution.id,
      postedBy: actor,
      approvedBy: distribution.approvedBy,
    };
    seq += 1;
    entries.push({
      ...base,
      id: `le-${distribution.id}-${line.holderId}-d`,
      seq,
      type: 'DISTRIBUTION',
      units: 0,
      amount: line.net,
      reference: `DIST ${distribution.periodEnd}`,
      memo: `${line.method === 'DRP' ? 'Reinvested' : 'Paid'} at ${distribution.centsPerUnit} cpu`,
    });
    const withheld = round2(line.tfnWithholding + line.nrWithholding);
    if (withheld > 0) {
      seq += 1;
      entries.push({
        ...base,
        id: `le-${distribution.id}-${line.holderId}-w`,
        seq,
        type: 'WITHHOLDING',
        units: 0,
        amount: withheld,
        reference: `WHT ${distribution.periodEnd}`,
        memo: line.tfnWithholding > 0 ? 'TFN not quoted (47%)' : 'Non-resident withholding',
      });
    }
    if (line.method === 'DRP') {
      seq += 1;
      const price = unitClass.unitPrice;
      entries.push({
        ...base,
        id: `le-${distribution.id}-${line.holderId}-r`,
        seq,
        type: 'DRP_ISSUE',
        units: Math.round((line.net / price) * 10_000) / 10_000,
        amount: line.net,
        unitPrice: price,
        reference: `DRP ${distribution.periodEnd}`,
        memo: `Reinvested at ${price.toFixed(4)}`,
      });
    }
  }
  return entries;
}
